import { useCallback, useMemo, useState } from "react";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { FormControlLabel, Switch, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import type { MRT_ColumnFiltersState, MRT_Row } from "material-react-table";
import type { Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import DateRangeFilterChip from "@/components/dateTime/dateRangeFilterChip";
import DateRangeFilterModal from "@/components/dateTime/dateRangeFilterModal";
import type { DateRangeFilterModalValue } from "@/components/dateTime/dateRangeFilterModal";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import NavigateToSubjectOverviewLink from "@/components/subjectOverview/navigateToSubjectOverviewLink";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { KaerIcon } from "@/icons/kaerIcon";
import { formatDateValue, getLmsCompanyDetailsRoute } from "@/components/lms/lms.utils";
import styles from "@/components/lms/lmsBillingTable.module.css";
import { enrolmentBillingEntityAdapter } from "@/components/lms/enrolmentBillingEntityAdapter";

export interface LmsBillingRow {
  company: {
    id: string;
    subject_id: string;
    name: string;
  };
  has_active_lms_contract: boolean;
  lms_contract_status: "active" | "ended" | "future" | "none";
  lms_contract_start_date: string | null;
  lms_contract_end_date: string | null;
  users_with_courses: number;
  users_assigned_to_lms_single_instruction: number;
  users_assigned_to_lms_bundle_5: number;
  users_assigned_to_lms_bundle_10: number;
  users_assigned_to_lms_bundle_20: number;
  lms_single_instruction_booked: number;
  lms_bundle_5_booked: number;
  lms_bundle_10_booked: number;
  lms_bundle_20_booked: number;
  bundle_concerns: Record<
    BillingBundleTierKey,
    {
      quantity_shortfall: number;
      users_over_course_limit: number;
    }
  >;
}

type BillingBundleTierKey =
  | "single_instruction"
  | "bundle_5"
  | "bundle_10"
  | "bundle_20";

type BillingAssignedUsersField =
  | "users_assigned_to_lms_single_instruction"
  | "users_assigned_to_lms_bundle_5"
  | "users_assigned_to_lms_bundle_10"
  | "users_assigned_to_lms_bundle_20";

const shouldHideBillingFigures = (row: LmsBillingRow): boolean =>
  row.users_with_courses === 0 && !row.lms_contract_start_date;

const initialColumnFilters: MRT_ColumnFiltersState = [
  { id: "lms_contract_status", value: "active" },
];

const exactNumericFilter = (
  row: Row<LmsBillingRow>, 
  columnId: string,
  filterValue: unknown
): boolean => Number(row.getValue(columnId)) === Number(filterValue);

exactNumericFilter.autoRemove = (filterValue: unknown): boolean =>
  filterValue === undefined ||
  filterValue === null ||
  filterValue === "" ||
  (typeof filterValue === "number" && Number.isNaN(filterValue));

const bundleTierByAssignedUsersColumn: Record<
  BillingAssignedUsersField,
  {
    bookedField: keyof LmsBillingRow;
    bookingCountTranslationKey: string;
    concernKey: BillingBundleTierKey;
    courseLimit: number;
  }
> = {
  users_assigned_to_lms_single_instruction: {
    bookedField: "lms_single_instruction_booked",
    bookingCountTranslationKey: "lms_billing_single_instruction_count",
    concernKey: "single_instruction",
    courseLimit: 1,
  },
  users_assigned_to_lms_bundle_5: {
    bookedField: "lms_bundle_5_booked",
    bookingCountTranslationKey: "lms_billing_bundle_count",
    concernKey: "bundle_5",
    courseLimit: 5,
  },
  users_assigned_to_lms_bundle_10: {
    bookedField: "lms_bundle_10_booked",
    bookingCountTranslationKey: "lms_billing_bundle_count",
    concernKey: "bundle_10",
    courseLimit: 10,
  },
  users_assigned_to_lms_bundle_20: {
    bookedField: "lms_bundle_20_booked",
    bookingCountTranslationKey: "lms_billing_bundle_count",
    concernKey: "bundle_20",
    courseLimit: 20,
  },
};

const billingSwitchStyles = {
  "& .MuiSwitch-switchBase": {
    color: "#8c1ec8",
    "& + .MuiSwitch-track": {
      backgroundColor: "#8c1ec8",
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#ff6428",
    "& + .MuiSwitch-track": {
      backgroundColor: "#ff6428",
    },
  },
};

const createBillingFigureColumnOptions = (t: TFunction) => ({
  filterFn: exactNumericFilter,
  setNoFilterParameter: true,
  muiFilterTextFieldProps: {
    type: "number",
    inputProps: { min: 0, step: 1 },
  },
  Cell: ({ cell, row }: any) => {
    if (shouldHideBillingFigures(row.original)) return "";

    const figure = cell.getValue();
    const bundleTier =
      bundleTierByAssignedUsersColumn[
        cell.column.id as BillingAssignedUsersField
      ];
    if (bundleTier) {
      const concern = row.original.bundle_concerns?.[bundleTier.concernKey];
      const concernMessages: string[] = [];
      if (concern?.quantity_shortfall > 0) {
        const bookedCount = t(bundleTier.bookingCountTranslationKey, {
          count: row.original[bundleTier.bookedField],
        });
        const userCount = t("lms_billing_user_count", { count: figure });
        concernMessages.push(
          t("lms_billing_quantity_shortfall_tooltip", {
            bookedCount,
            userCount,
          })
        );
      }
      if (concern?.users_over_course_limit > 0) {
        concernMessages.push(
          t("lms_billing_course_limit_tooltip", {
            count: concern.users_over_course_limit,
            courseLimit: bundleTier.courseLimit,
          })
        );
      }
      if (concernMessages.length > 0) {
        return (
          <Tooltip arrow title={concernMessages.join(" ")}>
            <span className={styles.overbookedFigure} tabIndex={0}>
              {figure}
            </span>
          </Tooltip>
        );
      }
    }
    return figure;
  },
});

export default function LmsBillingTable() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRangeFilterModalValue | []>(
    []
  );
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [consolidated, setConsolidated] = useState(true);
  const [onlyRowsWithConcerns, setOnlyRowsWithConcerns] = useState(false);
  const [startDate, endDate] = dateRange;
  const billingFigureColumnOptions = useMemo(
    () => createBillingFigureColumnOptions(t),
    [t]
  );

  const handleOpenCompanyDetails = useCallback(
    (company: LmsBillingRow["company"]) => {
      const companySubjectId = String(company?.subject_id ?? "").trim();
      if (!companySubjectId) {
        return;
      }

      dispatch(
        setSubjectSearch({
          value: companySubjectId,
          label: company.name,
          companyId: company.id,
          is_company: true,
        })
      );
      navigate(getLmsCompanyDetailsRoute());
    },
    [dispatch, navigate]
  );

  const billingRowActions = useCallback(
    (row: MRT_Row<LmsBillingRow>) => {
      const company = row.original.company;
      if (!String(company?.subject_id ?? "").trim()) {
        return null;
      }

      return (
        <Tooltip
          arrow
          placement="top"
          enterDelay={1000}
          enterNextDelay={250}
          title={t("lms_company_details")}
        >
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={() => handleOpenCompanyDetails(company)}
            aria-label={t("lms_company_details")}
          >
            <KaerIcon
              icon="lmsOverview"
              height="24"
              width="24"
              fill="#6c6c6c"
              stroke="#6c6c6c"
              strokeWidth="0"
            />
          </GenericButton>
        </Tooltip>
      );
    },
    [handleOpenCompanyDetails, t]
  );

  const columns = useMemo(
    () => [
      {
        header: t("company"),
        accessorKey: "company.name",
        id: "company_name",
        size: 300,
        Cell: ({ row }: { row: MRT_Row<LmsBillingRow> }) => {
          const company = row.original.company;
          const name = company?.name ?? "-";
          const companySubjectId = String(company?.subject_id ?? "").trim();
          if (companySubjectId) {
            return (
              <NavigateToSubjectOverviewLink
                subjectId={companySubjectId}
                label={name}
              />
            );
          }
          return name;
        },
      },
      {
        header: t("has_active_lms_contract"),
        accessorKey: "lms_contract_status",
        id: "lms_contract_status",
        filterVariant: "select",
        filterSelectOptions: [
          { value: "active", label: t("yes") },
          { value: "none", label: t("no") },
          {
            value: "future",
            label: t("lms_contract_starts_in_future"),
          },
          { value: "ended", label: t("lms_contract_ended") },
        ],
        setNoFilterParameter: true,
        Cell: ({ row }: any) => {
          if (row.original.lms_contract_status === "active") return t("yes");
          if (row.original.lms_contract_status === "future")
            return t("lms_contract_starts_in_future");
          if (row.original.lms_contract_status === "ended")
            return t("lms_contract_ended");
          return t("no");
        },
      },
      {
        header: t("lms_contract_start_date"),
        accessorKey: "lms_contract_start_date",
        id: "lms_contract_start_date",
        filterVariant: "date",
        filterDateRangeFormat: "date" as const,
        dateFilterApiParams: {
          exact: "lms_contract_start_date",
          gte: "lms_contract_start_date__gte",
          lte: "lms_contract_start_date__lte",
          gt: "lms_contract_start_date__gt",
          lt: "lms_contract_start_date__lt",
        },
        sortingFn: "date",
        Cell: ({ row }: any) => {
          const contractStartDate = row.original.lms_contract_start_date;
          return contractStartDate ? formatDateValue(contractStartDate) : "";
        },
      },
      {
        header: t("lms_contract_end_date"),
        accessorKey: "lms_contract_end_date",
        id: "lms_contract_end_date",
        filterVariant: "date",
        filterDateRangeFormat: "date" as const,
        dateFilterApiParams: {
          exact: "lms_contract_end_date",
          gte: "lms_contract_end_date__gte",
          lte: "lms_contract_end_date__lte",
          gt: "lms_contract_end_date__gt",
          lt: "lms_contract_end_date__lt",
        },
        sortingFn: "date",
        Cell: ({ row }: any) => {
          const contractEndDate = row.original.lms_contract_end_date;
          return contractEndDate ? formatDateValue(contractEndDate) : "";
        },
      },
      {
        header: t("lms_billing_users_with_courses"),
        accessorKey: "users_with_courses",
        id: "users_with_courses",
        ...billingFigureColumnOptions,
      },
      {
        header: t("lms_single_instruction_booked"),
        accessorKey: "lms_single_instruction_booked",
        id: "lms_single_instruction_booked",
        ...billingFigureColumnOptions,
      },
      {
        header: t("lms_billing_users_assigned_to_single_instruction"),
        accessorKey: "users_assigned_to_lms_single_instruction",
        id: "users_assigned_to_lms_single_instruction",
        ...billingFigureColumnOptions,
      },
      {
        header: t("lms_bundle_5_booked"),
        accessorKey: "lms_bundle_5_booked",
        id: "lms_bundle_5_booked",
        ...billingFigureColumnOptions,
      },
      {
        header: t("lms_billing_users_assigned_to_bundle_5"),
        accessorKey: "users_assigned_to_lms_bundle_5",
        id: "users_assigned_to_lms_bundle_5",
        ...billingFigureColumnOptions,
      },
      {
        header: t("lms_bundle_10_booked"),
        accessorKey: "lms_bundle_10_booked",
        id: "lms_bundle_10_booked",
        ...billingFigureColumnOptions,
      },
      {
        header: t("lms_billing_users_assigned_to_bundle_10"),
        accessorKey: "users_assigned_to_lms_bundle_10",
        id: "users_assigned_to_lms_bundle_10",
        ...billingFigureColumnOptions,
      },
      {
        header: t("lms_bundle_20_booked"),
        accessorKey: "lms_bundle_20_booked",
        id: "lms_bundle_20_booked",
        ...billingFigureColumnOptions,
      },
      {
        header: t("lms_billing_users_assigned_to_bundle_20"),
        accessorKey: "users_assigned_to_lms_bundle_20",
        id: "users_assigned_to_lms_bundle_20",
        ...billingFigureColumnOptions,
      },
    ],
    [billingFigureColumnOptions, t]
  );

  const additionalUrlParameter = useMemo(
    () => ({
      consolidated,
      only_rows_with_concerns: onlyRowsWithConcerns,
      ...(startDate && endDate
        ? {
            date_from: dayjs(startDate).format("YYYY-MM-DD"),
            date_to: dayjs(endDate).format("YYYY-MM-DD"),
          }
        : {}),
    }),
    [consolidated, endDate, onlyRowsWithConcerns, startDate]
  );

  const handleResetAllFilters = useCallback(() => {
    setDateRange([]);
    setConsolidated(true);
    setOnlyRowsWithConcerns(false);
  }, []);
  const hasAdditionalFiltersToReset =
    Boolean(startDate || endDate) || !consolidated || onlyRowsWithConcerns;

  const toolbar = () => (
    <div className={styles.toolbar}>
      <GenericButton
        startIcon={<TuneOutlinedIcon />}
        onClick={() => setIsDateRangeModalOpen(true)}
      >
        {t("lms_billing_enrolment_period")}
      </GenericButton>
      {startDate && endDate && (
        <DateRangeFilterChip
          filterLabel={t("lms_billing_enrolment_period")}
          startDate={startDate}
          endDate={endDate}
          onDelete={() => setDateRange([])}
          onClick={() => setIsDateRangeModalOpen(true)}
        />
      )}
      <FormControlLabel
        label={
          consolidated
            ? t("lms_billing_by_customer_number")
            : t("company_show_all_hierarchy")
        }
        control={
          <Switch
            checked={consolidated}
            onChange={(event) => setConsolidated(event.target.checked)}
            sx={billingSwitchStyles}
          />
        }
      />
      <FormControlLabel
        label={
          onlyRowsWithConcerns
            ? t("lms_billing_flagged_entries_only")
            : t("lms_billing_all_entries")
        }
        control={
          <Switch
            checked={onlyRowsWithConcerns}
            onChange={(event) => setOnlyRowsWithConcerns(event.target.checked)}
            sx={billingSwitchStyles}
          />
        }
      />
    </div>
  );

  return (
    <>
      <GenericCrudTable
        entity="enrolment/billing"
        entityAdapter={enrolmentBillingEntityAdapter}
        columns={columns}
        additionalUrlParameter={additionalUrlParameter}
        customTopToolBarAction={toolbar}
        customRowActions={billingRowActions}
        customRowActionColSize={70}
        readOnlyMode
        disableAddItem
        hideEditButtonForRows
        hideDeleteButtonForRows
        enableRowSelection={false}
        enableBulkDelete={false}
        csvExportUrl={null}
        disableGlobalFilter
        allowColumnFiltering
        showColumnFilterbyDefault
        columnMinSize={40}
        initialColumnFilters={initialColumnFilters}
        hasAdditionalFiltersToReset={hasAdditionalFiltersToReset}
        onResetAllFilters={handleResetAllFilters}
        shouldNotImpersonate
      />
      {isDateRangeModalOpen && (
        <DateRangeFilterModal
          open
          title={t("lms_billing_choose_enrolment_period")}
          description={t("lms_billing_period_description")}
          initialDateRange={dateRange}
          onClose={() => setIsDateRangeModalOpen(false)}
          onApply={(nextDateRange) => {
            setDateRange(nextDateRange);
            setIsDateRangeModalOpen(false);
          }}
        />
      )}
    </>
  );
}
